<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { resolveImageAlt } from "@/utils/imageAlt";

const { gsap, loadGSAP } = useGSAP();
const pageRef = usePage();
const img = useImage();

const videoEl = ref<HTMLVideoElement | null>(null);
const videoPlaying = ref(false);
const videoSrc = ref("");

const page = computed(() => pageRef.value);

const posterImage = computed(() => {
  const p = page.value;
  return p?.heroVideoPoster || p?.trekVideoPoster || p?.heroImage || null;
});

const posterSrc = computed(() => {
  const image = posterImage.value;
  if (!image) return "";
  return image.urlMobile || image.url || "";
});

const posterDesktopSrc = computed(
  () => posterImage.value?.url || posterSrc.value,
);

const posterWebp = computed(() => {
  if (!posterDesktopSrc.value) return "";
  return img(posterDesktopSrc.value, {
    width: 1780,
    format: "webp",
    quality: 75,
  });
});

const posterMobileWebp = computed(() => {
  if (!posterSrc.value) return "";
  return img(posterSrc.value, { width: 900, format: "webp", quality: 75 });
});

const posterAlt = computed(() =>
  resolveImageAlt(
    posterImage.value?.alt,
    page.value?.heroVideoTitle || page.value?.title,
  ),
);

onMounted(() => {
  loadGSAP();
  const p = page.value;
  const mobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 991px)").matches;
  videoSrc.value = mobile
    ? p?.heroVimeoUrlMobile || p?.heroVimeoUrl || ""
    : p?.heroVimeoUrl || p?.heroVimeoUrlMobile || "";

  // Defer autoplay past first paint so the poster can be LCP
  const start = () => {
    const el = videoEl.value;
    if (!el || !videoSrc.value) return;
    el.muted = true;
    el.play()
      .then(() => {
        videoPlaying.value = true;
      })
      .catch(() => {
        // Keep poster visible if autoplay is blocked
      });
  };

  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(start, { timeout: 2000 });
  } else {
    setTimeout(start, 400);
  }
});

useHead({
  link: computed(() => {
    const links: Array<Record<string, string>> = [];
    if (posterMobileWebp.value) {
      links.push({
        rel: "preload",
        as: "image",
        href: posterMobileWebp.value,
        media: "(max-width: 991px)",
        fetchpriority: "high",
      });
    }
    if (posterWebp.value) {
      links.push({
        rel: "preload",
        as: "image",
        href: posterWebp.value,
        media: "(min-width: 992px)",
        fetchpriority: "high",
      });
    }
    return links;
  }),
});

const scrollTo = () => {
  if (gsap.value) {
    gsap.value.to(window, {
      duration: 1,
      scrollTo: {
        y: "#trekPackages",
        offsetY: 300,
      },
      ease: "power2.inOut",
    });
  }
};

const scrollToIntro = () => {
  if (gsap.value) {
    gsap.value.to(window, {
      duration: 1,
      scrollTo: {
        y: ".treks-intro-section",
        offsetY: 300,
      },
      ease: "power2.inOut",
    });
  }
};
</script>

<template>
  <div
    class="main-hero hero"
    :class="{
      'hero-height-small':
        page?.template !== 'home' &&
        page?.template !== 'treks' &&
        page?.template !== 'trek-landing-page',
    }"
  >
    <div class="video-hero">
      <AppSVGBrandGraphic />
      <div class="chevron-container" @click.prevent="scrollToIntro">
        <svg class="chevron" width="40" height="40" viewBox="0 0 24 24">
          <path
            d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.59L18 10l-6 6z"
            fill="white"
          ></path>
        </svg>
        <svg class="chevron" width="40" height="40" viewBox="0 0 24 24">
          <path
            d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.59L18 10l-6 6z"
            fill="white"
          ></path>
        </svg>
      </div>
      <div
        v-if="page?.template === 'home' || page?.template === 'treks'"
        class="chevron-container"
        @click.prevent="scrollToIntro"
      >
        <svg class="chevron" width="40" height="40" viewBox="0 0 24 24">
          <path
            d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.59L18 10l-6 6z"
            fill="white"
          ></path>
        </svg>
        <svg class="chevron" width="40" height="40" viewBox="0 0 24 24">
          <path
            d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.59L18 10l-6 6z"
            fill="white"
          ></path>
        </svg>
      </div>
      <AppHeroCta />
      <div class="page-title-wrapper">
        <div class="page-title">
          <h2>{{ page?.heroVideoTitle }}</h2>
          <h3>{{ page?.heroVideoSubTitle }}</h3>
          <div
            v-if="page?.template === 'treks'"
            class="hero-cta-btn btn from-left"
            @click.prevent="scrollTo"
          >
            View Trek Packages
          </div>
        </div>
      </div>
      <div class="hero-fade"></div>
      <div class="vimeo-wrapper">
        <!-- SSR LCP poster — stays until video actually plays -->
        <picture
          v-if="posterSrc"
          class="hero-lcp-poster"
          :class="{ 'is-hidden': videoPlaying }"
        >
          <source
            v-if="posterMobileWebp"
            media="(max-width: 991px)"
            :srcset="posterMobileWebp"
            type="image/webp"
          />
          <source
            v-if="posterWebp"
            media="(min-width: 992px)"
            :srcset="posterWebp"
            type="image/webp"
          />
          <img
            :src="posterSrc"
            :alt="posterAlt"
            :width="posterImage?.width || 1200"
            :height="posterImage?.height || 675"
            fetchpriority="high"
            decoding="async"
          />
        </picture>
        <video
          ref="videoEl"
          playsinline
          muted
          loop
          preload="none"
          class="hero-video"
          :poster="posterDesktopSrc || posterSrc"
        >
          <source v-if="videoSrc" :src="videoSrc" type="video/mp4" />
        </video>
      </div>
    </div>
    <AppAnnouncement v-if="page?.template !== 'trek-landing-page'" />
  </div>
</template>
